/**
 * Sinistres configurable components (Phase 5 — fiches 22736 / 22807 / 22740
 * / 22742 / 22744 / 22746):
 *
 * | id | host | stable keys |
 * |---|---|---|
 * | `extranet.claimsListCard` | `ClaimsListCard` (DataTableCard, detached server-side toolbar `q` / `etat`) | columns `assure`, `produit`, `numeroContrat`, `reference`, `dateSurvenance`, `circonstances`, `coutAssureur`, `statut`; filter `statut`; search |
 * | `extranet.claimDetailsCard` | `EntityInfoCard` | items `reference`, `statut`, `produit`, `dateSurvenance`, `dateDeclaration`, `type`, `coutAssureur`, `circonstances`, `description`, `dommages` |
 * | `extranet.claimStakeholdersCard` | `ClaimStakeholdersCard` (DataTableCard) | columns `nature`, `nomPrenom`, `email`, `telephone` |
 * | `extranet.claimChargesCard` | `ClaimChargesCard` (DataTableCard) | columns `garantie`, `provision`, `reglement`, `recoursEstime`, `recoursEncaisse`, `franchise` (+ row `total`) |
 * | `extranet.claimDocumentsCard` | `DocumentsByCategoryCard` | items `nom`, `date` |
 * | `extranet.claimPiecesCard` | `PiecesCard` + upload (22976) | items `libelle`, `statut`, `dateDemande`, `depot`; action `submit` |
 *
 * Each usage binds its config props (`componentId`, `fields`) with
 * `bindCardProps`, is registered idempotently (`registerOnce`) with its
 * data-props Zod schema, and is exported wrapped with `withComponentRules`
 * as `<Name>Configurable` — the pages import those. Every surface also takes
 * a `setTitle` rule (no base title: the page header / the tab names it).
 */

import { withComponentRules } from '@components/shared/configurable';

import { extranetBaseContextSchema } from '../../contextVariables';
import { EXTRANET_MODULE, registerOnce } from '../registerHelpers';
import { bindCardProps } from '../shared/bindCardProps';
import { DocumentsByCategoryCard } from '../shared/DocumentsByCategoryCard';
import { EntityInfoCard } from '../shared/EntityInfoCard';
import { PiecesCard } from '../shared/PiecesCard';
import { PIECE_UPLOAD_CONFIG } from '../shared/pieceUploadConfig';
import {
  DOCUMENTS_PREVIEW_PROPS,
  SHARED_CARD_PROP_LABELS,
  documentsCardDataSchema,
  entityInfoCardDataSchema,
  piecesCardDataSchema,
  piecesUploadPreviewProps,
  type DocumentsCardDataProps,
  type EntityInfoCardDataProps,
  type PiecesCardDataProps,
} from '../shared/schemas';
import { ClaimChargesCard } from './ClaimChargesCard';
import { claimDetailContextSchema } from './claimContext';
import { CLAIM_DETAILS_FIELDS } from './claimDetailsConfig';
import { ClaimsListCard } from './ClaimsListCard';
import { ClaimStakeholdersCard } from './ClaimStakeholdersCard';
import {
  CLAIM_CHARGES_PREVIEW_PROPS,
  CLAIM_DETAILS_PREVIEW_VALUES,
  CLAIM_STAKEHOLDERS_PREVIEW_PROPS,
  CLAIMS_LIST_PREVIEW_PROPS,
  claimChargesCardDataSchema,
  claimStakeholdersCardDataSchema,
  claimsListCardDataSchema,
  type ClaimChargesCardDataProps,
  type ClaimStakeholdersCardDataProps,
  type ClaimsListCardDataProps,
} from './schemas';

/** Registered ids of the Sinistres surfaces. */
export const CLAIM_COMPONENT_IDS = {
  list: 'extranet.claimsListCard',
  details: 'extranet.claimDetailsCard',
  stakeholders: 'extranet.claimStakeholdersCard',
  charges: 'extranet.claimChargesCard',
  documents: 'extranet.claimDocumentsCard',
  pieces: 'extranet.claimPiecesCard',
} as const;

// ---------------------------------------------------------------------------
// Usages (config props bound)
// ---------------------------------------------------------------------------

export const ClaimsListUsage = bindCardProps(ClaimsListCard, {
  componentId: CLAIM_COMPONENT_IDS.list,
});

export const ClaimDetailsUsage = bindCardProps(EntityInfoCard, {
  componentId: CLAIM_COMPONENT_IDS.details,
  fields: CLAIM_DETAILS_FIELDS,
});

export const ClaimStakeholdersUsage = bindCardProps(ClaimStakeholdersCard, {
  componentId: CLAIM_COMPONENT_IDS.stakeholders,
});

export const ClaimChargesUsage = bindCardProps(ClaimChargesCard, {
  componentId: CLAIM_COMPONENT_IDS.charges,
});

export const ClaimDocumentsUsage = bindCardProps(DocumentsByCategoryCard, {
  componentId: CLAIM_COMPONENT_IDS.documents,
});

/** Pièces tab: piece upload on (fiche 22976 — `depot` zone on each pending piece). */
export const ClaimPiecesUsage = bindCardProps(PiecesCard, {
  componentId: CLAIM_COMPONENT_IDS.pieces,
  upload: PIECE_UPLOAD_CONFIG,
});

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

const ROWS_PROP_LABELS = {
  rows: SHARED_CARD_PROP_LABELS.rows,
  loading: SHARED_CARD_PROP_LABELS.loading,
  error: SHARED_CARD_PROP_LABELS.error,
} as const;

/** Register the six Sinistres components. Idempotent (`registerOnce`). */
export function registerClaimComponents(): void {
  registerOnce<ClaimsListCardDataProps>({
    id: CLAIM_COMPONENT_IDS.list,
    displayName: 'Liste des sinistres',
    module: EXTRANET_MODULE,
    component: ClaimsListUsage,
    propsSchema: claimsListCardDataSchema,
    defaultPropLabels: ROWS_PROP_LABELS,
    previewProps: CLAIMS_LIST_PREVIEW_PROPS,
    contextVariables: extranetBaseContextSchema,
  });

  registerOnce<EntityInfoCardDataProps>({
    id: CLAIM_COMPONENT_IDS.details,
    displayName: 'Détails du sinistre',
    module: EXTRANET_MODULE,
    component: ClaimDetailsUsage,
    propsSchema: entityInfoCardDataSchema,
    defaultPropLabels: {
      values: SHARED_CARD_PROP_LABELS.values,
      loading: SHARED_CARD_PROP_LABELS.loading,
      error: SHARED_CARD_PROP_LABELS.error,
    },
    previewProps: { values: CLAIM_DETAILS_PREVIEW_VALUES },
    contextVariables: claimDetailContextSchema,
  });

  registerOnce<ClaimStakeholdersCardDataProps>({
    id: CLAIM_COMPONENT_IDS.stakeholders,
    displayName: 'Intervenants du sinistre',
    module: EXTRANET_MODULE,
    component: ClaimStakeholdersUsage,
    propsSchema: claimStakeholdersCardDataSchema,
    defaultPropLabels: ROWS_PROP_LABELS,
    previewProps: CLAIM_STAKEHOLDERS_PREVIEW_PROPS,
    contextVariables: claimDetailContextSchema,
  });

  registerOnce<ClaimChargesCardDataProps>({
    id: CLAIM_COMPONENT_IDS.charges,
    displayName: 'Charges du sinistre',
    module: EXTRANET_MODULE,
    component: ClaimChargesUsage,
    propsSchema: claimChargesCardDataSchema,
    defaultPropLabels: ROWS_PROP_LABELS,
    previewProps: CLAIM_CHARGES_PREVIEW_PROPS,
    contextVariables: claimDetailContextSchema,
  });

  registerOnce<DocumentsCardDataProps>({
    id: CLAIM_COMPONENT_IDS.documents,
    displayName: 'Documents du sinistre',
    module: EXTRANET_MODULE,
    component: ClaimDocumentsUsage,
    propsSchema: documentsCardDataSchema,
    defaultPropLabels: {
      documents: SHARED_CARD_PROP_LABELS.documents,
      loading: SHARED_CARD_PROP_LABELS.loading,
      error: SHARED_CARD_PROP_LABELS.error,
    },
    previewProps: DOCUMENTS_PREVIEW_PROPS,
    contextVariables: claimDetailContextSchema,
  });

  registerOnce<PiecesCardDataProps>({
    id: CLAIM_COMPONENT_IDS.pieces,
    displayName: 'Pièces du sinistre',
    module: EXTRANET_MODULE,
    component: ClaimPiecesUsage,
    propsSchema: piecesCardDataSchema,
    defaultPropLabels: {
      pieces: SHARED_CARD_PROP_LABELS.pieces,
      loading: SHARED_CARD_PROP_LABELS.loading,
      error: SHARED_CARD_PROP_LABELS.error,
    },
    previewProps: piecesUploadPreviewProps('sinistre'),
    contextVariables: claimDetailContextSchema,
  });
}

// ---------------------------------------------------------------------------
// Rule-aware exports (pages import these)
// ---------------------------------------------------------------------------

export const ClaimsListCardConfigurable = withComponentRules(
  ClaimsListUsage,
  CLAIM_COMPONENT_IDS.list,
);

export const ClaimDetailsCardConfigurable = withComponentRules(
  ClaimDetailsUsage,
  CLAIM_COMPONENT_IDS.details,
);

export const ClaimStakeholdersCardConfigurable = withComponentRules(
  ClaimStakeholdersUsage,
  CLAIM_COMPONENT_IDS.stakeholders,
);

export const ClaimChargesCardConfigurable = withComponentRules(
  ClaimChargesUsage,
  CLAIM_COMPONENT_IDS.charges,
);

export const ClaimDocumentsCardConfigurable = withComponentRules(
  ClaimDocumentsUsage,
  CLAIM_COMPONENT_IDS.documents,
);

export const ClaimPiecesCardConfigurable = withComponentRules(
  ClaimPiecesUsage,
  CLAIM_COMPONENT_IDS.pieces,
);
